// SEO helper — client-side meta injection + JSON-LD
import { useEffect } from "react";
import { SITE } from "@/config/site";

const upsertMeta = (attr, key, value) => {
  if (!value) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};

const upsertLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const upsertJsonLd = (id, data) => {
  if (!data) return;
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const removeById = (id) => {
  const el = document.getElementById(id);
  if (el) el.remove();
};

export const SEO = ({
  title,
  description,
  path = "/",
  ogImage,
  jsonLd = null, // single object OR array
  breadcrumb = null, // [{name, url}]
}) => {
  const canonical = `${SITE.domain}${path}`;
  const fullTitle = title || `${SITE.name} — ${SITE.tagline}`;
  const desc = description || SITE.description;
  const img = ogImage || "https://images.pexels.com/photos/37949152/pexels-photo-37949152.jpeg?auto=compress&cs=tinysrgb&w=1200";

  useEffect(() => {
    document.title = fullTitle;
    upsertMeta("name", "description", desc);
    upsertLink("canonical", canonical);

    // Open Graph
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", img);
    upsertMeta("property", "og:site_name", SITE.name);

    // Twitter
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", desc);
    upsertMeta("name", "twitter:image", img);

    // TravelAgency schema (on every page)
    upsertJsonLd("ld-travelagency", {
      "@context": "https://schema.org",
      "@type": "TravelAgency",
      name: SITE.name,
      url: SITE.domain,
      description: SITE.description,
      telephone: SITE.phone,
      email: SITE.enquiryEmail,
      address: {
        "@type": "PostalAddress",
        streetAddress: "Plot No 69, Avenue 77, Sector 77",
        addressLocality: "Gurgaon",
        addressRegion: "Haryana",
        postalCode: "122004",
        addressCountry: "IN",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: 28.389173,
        longitude: 76.9785332,
      },
      hasMap: SITE.mapsUrl,
      areaServed: ["Andaman", "Goa", "Rajasthan", "Ladakh", "India"],
      sameAs: [SITE.socials.instagram, SITE.socials.facebook, SITE.socials.youtube],
    });

    // Breadcrumb
    if (breadcrumb && breadcrumb.length) {
      upsertJsonLd("ld-breadcrumb", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumb.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: `${SITE.domain}${b.url}`,
        })),
      });
    } else {
      removeById("ld-breadcrumb");
    }

    // Page-specific JSON-LD (blog, package, faq, etc)
    if (jsonLd) {
      const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      arr.forEach((data, i) => upsertJsonLd(`ld-page-${i}`, data));
    } else {
      // Clean up up to 5 page-specific slots
      for (let i = 0; i < 6; i++) removeById(`ld-page-${i}`);
    }
  }, [fullTitle, desc, canonical, img, JSON.stringify(jsonLd), JSON.stringify(breadcrumb)]);

  return null;
};
